'use client'

import { useState } from 'react'
import { useTranslations } from '@/lib/next-shim'
import { Link } from '@/lib/i18n/settings'
import { useGetMyInfosQuery } from '@/core/graphql/hooks'
import { useIsFranceMode } from '@/hooks/useIsFranceMode'
import { isPatientReady } from '@/utils/profile-readiness'

/**
 * Non-blocking nudge: patients can browse/book with an incomplete profile, but they must
 * complete it (France: incl. NIR) before a video consultation. This banner points them
 * there without forcing a redirect.
 */
export function ProfileCompletionBanner() {
  const t = useTranslations('page.AccountDashboard.completionBanner')
  const isFrance = useIsFranceMode()
  const { data } = useGetMyInfosQuery()
  const [dismissed, setDismissed] = useState(false)

  if (dismissed || !data?.myInfos || isPatientReady(data.myInfos, isFrance)) return null

  return (
    <div
      role="status"
      className="mx-5 flex items-start justify-between gap-4 rounded-lg border border-amber-200 bg-amber-50 p-4"
      data-testid="profile-completion-banner"
    >
      <div>
        <p className="font-medium text-amber-900">{t('title')}</p>
        <p className="mt-0.5 text-sm text-amber-800">{t('description')}</p>
        <Link
          href="/complete-profile?callbackUrl=/account/dashboard"
          className="mt-2 inline-block text-sm font-semibold text-amber-900 underline hover:text-amber-700"
        >
          {t('cta')}
        </Link>
      </div>
      <button
        type="button"
        aria-label={t('dismiss')}
        onClick={() => setDismissed(true)}
        className="shrink-0 rounded p-1 text-amber-700 hover:bg-amber-100 hover:text-amber-900"
      >
        ✕
      </button>
    </div>
  )
}
